import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "rsuite";
import CloseIcon from "../../../../../assets/images/icons/CloseIcon.tsx";
import ThumbDown from "../../../../../assets/images/icons/ThumbDown.tsx";
import ThumbUp from "../../../../../assets/images/icons/ThumbUp.tsx";
import SubmittedFeedbackSVG from "../../../../../assets/images/svgs/SubmittedFeedback.svg";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaButtonGroup from "../../../../../components/common/BriaButtonGroup/BriaButtonGroup";
import BriaTooltip from "../../../../../components/common/BriaTooltip/BriaTooltip";
import { APPS, LABS } from "../../../../../constants/AppsConstants.ts";
import RouterConstants from "../../../../../constants/RouterConstants.ts";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import InputLayout from "../../../../../layout/InputLayout/InputLayout.tsx";
import { ImageEditingViewMode, ImageToImageConfigType, PlaygroundImage } from "../../../../../models/image-to-image";
import { TextToImageConfigType } from "../../../../../models/text-to-image.ts";
import { isFoxApps } from "../../../../../utils/index.ts";
import { FiboEditConfigType } from "../../../../FiboEdit/fibo-edit-store.tsx";
import classes from "../../../../auth/UserInfo/UserInfo.module.scss";
import { Feedback, FeedbackTypeEnum } from "../PlaygroundImageOverlay.tsx";
import ImageNegativeFeedbackCard from "./ImageNegativeFeedbackCard/ImageNegativeFeedbackCard";
import styles from "./ImageOverlayFeedbackButtons.module.scss";

const feedbackAppNameMap: Partial<{ [k in APPS | LABS | "upload" | "fox"]: string }> = {
	fox: "Fox",
	imageToImage: "Image To Image",
	textToImage: "Text To Image",
	productPlacement: "Product Placement",
	tgImageGeneration: "TG Image Generation",
	automotive: "Automotive",
};

type Props = {
	image: PlaygroundImage;
	thumbUpIcon?: React.ComponentType<any>;
	thumbDownIcon?: React.ComponentType<any>;
	highlightFeedbackButtons?: boolean;
};

const ImageOverlayFeedbackButtons = ({
	image,
	thumbUpIcon: ThumbUpIconComponent,
	thumbDownIcon: ThumbDownIconComponent,
	highlightFeedbackButtons = false,
}: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.imageOverlay" });
	const { uiStore, playgroundStore } = useAppStore();
	const [openFeedbackBox, setOpenFeedbackBox] = useState(false);
	const [feedbackText, setFeedbackText] = useState<string>("");
	const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState<boolean>(false);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
	const [loadingFeedbackType, setLoadingFeedbackType] = useState<FeedbackTypeEnum | null>(null);
	const isEvaluationPlayground =
		playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION ||
		window.location.pathname.includes(RouterConstants.NEW_TG.MODELS.EVALUATION.path);
	const isSingleMode = playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE;

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event: any) => {
		event.stopPropagation();
		// Get all dislike icons and check if one of them was clicked
		const elements = document.querySelectorAll(".dislikeIconRef") ?? [];
		let isDislikeClicked = false;
		elements.forEach((element) => {
			if ((element as HTMLElement).contains(event.target)) {
				isDislikeClicked = true;
			}
		});

		if (isDislikeClicked) {
			// If one of dislikes was clicked, hide all feedback forms and show the relevant one
			const feedbackElements = document.querySelectorAll(".feedbackContainerRef") ?? [];
			feedbackElements.forEach((element) => {
				(element as HTMLElement).style.display = "none";
			});
			const closestFeedbackContainerRef = event.target
				?.closest(`.${styles.feedbackWrapper}`)
				?.querySelector(".feedbackContainerRef");
			if (closestFeedbackContainerRef) {
				closestFeedbackContainerRef.style.display = "flex";
			}

			// setOpenFeedbackBox(true);
		} else {
			uiStore.hideAllFeedbackBoxes(event);
		}
	};

	const sendFeedback = async (e: any, type: FeedbackTypeEnum, imageUrl: string) => {
		e.stopPropagation();
		setLoadingFeedbackType(type);

		try {
			// For fibo edit, treat it as IMAGE_TO_IMAGE for feedback purposes
			const isFiboEdit = image.type === LABS.FIBO_EDIT;
			const imageTypeForFeedback = isFiboEdit ? APPS.IMAGE_TO_IMAGE : image.type;

			const appName =
				isFoxApps() && imageTypeForFeedback == APPS.IMAGE_TO_IMAGE
					? feedbackAppNameMap.fox
					: feedbackAppNameMap[imageTypeForFeedback ?? "upload"];

			let feedbackIssues: string | undefined = undefined;
			if (type === FeedbackTypeEnum.Negative && selectedIssues.length > 0) {
				const labels = selectedIssues.map((k) => t(`disLike.issues.${k}`) as unknown as string).filter(Boolean);
				if (labels.length) {
					feedbackIssues = labels.join(", ");
				}
			}

			// For fibo edit, we need to format the config to match image editing feedback structure
			let configs = image.config as TextToImageConfigType | ImageToImageConfigType;
			let image_config = image.image_config as TextToImageConfigType | ImageToImageConfigType;

			if (isFiboEdit) {
				const parentResult = playgroundStore.playgroundResults.find((result) =>
					result.images.some((img) => img.id === image.id),
				);
				if (parentResult?.config) {
					const fiboConfig = parentResult.config as FiboEditConfigType;
					// Transform fibo edit config to match image editing feedback structure
					configs = {
						original_image: { url: fiboConfig.image },
					} as any;

					image_config = {
						num_results: fiboConfig.num_results,
						object_manipulation: {
							...(fiboConfig.mask && { mask: fiboConfig.mask }),
							...(fiboConfig.prompt && { prompt: fiboConfig.prompt }),
							operation: "fibo_edit",
						},
					} as any;
				}
			}

			const newFeedback: Feedback = {
				appName: appName ?? "Uploaded",
				type: type,
				imageUrl: imageUrl,
				text: feedbackText,
				...(feedbackIssues && { feedback_issues: feedbackIssues }),
				configs: configs,
				image_config: image_config,
				mode: image.mode,
				seed: image.seed ?? 0,
				ui_type: image.ui_type,
				...(image.ref_image_url && { refImageUrl: image.ref_image_url }),
				modelName: (image_config as TextToImageConfigType)?.model_name,
			};
			if (appName === feedbackAppNameMap.textToImage) {
				newFeedback.modelId =
					((configs as TextToImageConfigType)?.model_id as string) ||
					((image_config as TextToImageConfigType)?.model_id as string);
			} else if (appName === feedbackAppNameMap.tgImageGeneration) {
				newFeedback.modelId =
					((configs as TextToImageConfigType)?.tailored_model_id as string) ||
					((image_config as TextToImageConfigType)?.tailored_model_id as string);
			}

			const alreadySent = playgroundStore.feedbackHistory.find(
				(feedback) => feedback.imageUrl === imageUrl && feedback.type === type,
			);

			if (type === FeedbackTypeEnum.Positive) {
				setOpenFeedbackBox(false);
			}

			if (!alreadySent) {
				await playgroundStore.addOrUpdateFeedback(newFeedback);
			}
			setFeedbackText("");
			setSelectedIssues([]);
			if (type === FeedbackTypeEnum.Negative) {
				setIsFeedbackSubmitted(true);
			}
			setTimeout(() => {
				uiStore.hideAllFeedbackBoxes(e);
				setOpenFeedbackBox(false);
			}, 2000);
		} finally {
			setLoadingFeedbackType(null);
		}
	};

	return (
		<Box className={styles.imageOverlayFeedbackButtonsContainer}>
			<Box className={styles.feedbackWrapper}>
				<BriaButtonGroup
					className={clsx(styles.feedbackBtns, {
						[styles.singleMode]: isSingleMode,
						[styles.feedbackButtonsGlow]: highlightFeedbackButtons,
					})}
				>
					<BriaTooltip title={t("like")}>
						<IconButton
							className={clsx("iconHover", styles.icon, {
								[styles.selected]: playgroundStore.feedbackHistory.find(
									(feedback) =>
										feedback.imageUrl === image.url && feedback.type === FeedbackTypeEnum.Positive,
								),
							})}
							onClick={(e) => sendFeedback(e, FeedbackTypeEnum.Positive, image.url)}
							disabled={loadingFeedbackType !== null}
						>
							{loadingFeedbackType === FeedbackTypeEnum.Positive ? (
								<CircularProgress color="inherit" size={16} />
							) : ThumbUpIconComponent ? (
								<ThumbUpIconComponent />
							) : (
								<ThumbUp />
							)}
						</IconButton>
					</BriaTooltip>
					<BriaTooltip title={t("disLike.tooltip")}>
						<IconButton
							className={clsx(styles.icon, "dislikeIconRef", "iconHover", {
								[styles.selected]: playgroundStore.feedbackHistory.find(
									(feedback) =>
										feedback.imageUrl === image.url && feedback.type === FeedbackTypeEnum.Negative,
								),
							})}
							onClick={(e) => {
								e.stopPropagation();
								setIsFeedbackSubmitted(false);
								setAnchorEl(e.currentTarget as HTMLElement);
								setOpenFeedbackBox((prev) => !prev);
							}}
							disabled={loadingFeedbackType !== null}
						>
							{loadingFeedbackType === FeedbackTypeEnum.Negative ? (
								<CircularProgress color="inherit" size={16} />
							) : ThumbDownIconComponent ? (
								<ThumbDownIconComponent />
							) : (
								<ThumbDown />
							)}
						</IconButton>
					</BriaTooltip>
				</BriaButtonGroup>

				{isEvaluationPlayground ? (
					<ImageNegativeFeedbackCard
						open={openFeedbackBox}
						anchorEl={anchorEl}
						value={feedbackText}
						onChange={setFeedbackText}
						selectedIssues={selectedIssues}
						onToggleIssue={(key) => {
							setSelectedIssues((prev) =>
								prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
							);
						}}
						onClose={(event) => {
							event?.stopPropagation?.();
							setOpenFeedbackBox(false);
						}}
						onSubmit={(event) => {
							event?.stopPropagation?.();
							sendFeedback(event, FeedbackTypeEnum.Negative, image.url);
						}}
						loading={playgroundStore.loadingFeedback}
						submitted={playgroundStore.feedbackHistory.some(
							(feedback) =>
								feedback.imageUrl === image.url && feedback.type === FeedbackTypeEnum.Negative,
						)}
					/>
				) : (
					<Box
						className={clsx({
							[styles.feedbackContainer]: true,
							[styles.singleMode]: playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE,
							["feedbackContainerRef"]: true,
							[styles.hidden]: !openFeedbackBox,
						})}
					>
						<Box className={styles.feedbackElements}>
							{!isFeedbackSubmitted ? (
								<Box className={styles.feedbackInput}>
									<InputLayout className={styles.inputText} label={t("disLike.label")}>
										<TextField
											value={feedbackText}
											onChange={(e) => {
												setFeedbackText(e.target.value);
											}}
											onClick={(e) => {
												e.stopPropagation();
											}}
											placeholder={t("disLike.placeholder")}
											multiline
											maxRows={3}
											InputProps={{
												classes: {
													root: styles.bigTextField,
												},
											}}
											autoComplete="off"
										/>
									</InputLayout>
									<BriaButton
										className={styles.button}
										buttonType="primary"
										onClick={(e) => {
											e.stopPropagation();
											sendFeedback(e, FeedbackTypeEnum.Negative, image.url);
										}}
									>
										{playgroundStore.loadingFeedback ? (
											<Box className={classes.circleLoader}>
												{playgroundStore.loadingFeedback && (
													<CircularProgress color="inherit" size={15} />
												)}
											</Box>
										) : (
											<>{t("disLike.button")}</>
										)}
									</BriaButton>
								</Box>
							) : (
								<Box className={styles.feedbackSubmitted}>
									<img className={styles.feedbackSubmittedIcon} src={SubmittedFeedbackSVG} />
									<Typography className={styles.feedbackSubmittedMessage}>
										{t("disLike.feedback1")}
										<br />
										{t("disLike.feedback2")}
									</Typography>
									<CloseIcon
										onClick={(event) => {
											event.stopPropagation();
											uiStore.hideAllFeedbackBoxes(event);
										}}
										className={clsx({
											[styles.closeIcon]: true,
											["closeIcon"]: true,
										})}
									/>
								</Box>
							)}
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};
export default observer(ImageOverlayFeedbackButtons);
